import {
  BaseTable,
  Button,
  Checkbox,
  Flexbox,
  IBaseTableProps,
  Menu,
  NavBar,
  Sizes
} from "@sede-x/shell-ds-react-framework";
import {
  ArrowCcwCircle,
  CrossCircle,
  Edit,
  FileXlsOutline,
  TrashAltSolid
} from "@sede-x/shell-ds-react-framework/build/esm/components/Icon/components";
import {
  CellContext,
  ColumnDef,
  getPaginationRowModel,
  getSortedRowModel,
  Row,
  SortingState
} from "@tanstack/react-table";
import calculateSize from "calculate-size";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { TLinkedTicket, TTicket } from "ticketing/ticketing.types";
import { utils, writeFile } from "xlsx";
import AddOrUpdateTicket from "./AddOrUpdateTicket";

const PADDING = 50;
const FONT_OPTIONS = {
  font: "14px Segoe UI",
  fontWeight: "normal"
};

// Helper for getting nested property values
const propertyGetter = (path: string) => {
  return <T extends TGridTicket>(obj: T): unknown => {
    return path.split(".").reduce((prev: unknown, curr: string) => {
      return prev && typeof prev === "object" ? (prev as Record<string, unknown>)[curr] : null;
    }, obj as unknown);
  };
};

// Helper for formatting dates
const formatDateForDisplay = (date: Date | null | undefined) => {
  if (!date) {
    return "";
  }
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

export type TGridTicket = Omit<TLinkedTicket, "actualizedVolume" | "railcars"> & {
  grossVolume: number;
  railcars?: string;
  netVolume: number;
  volumeUnit: string;
  deliveryIds: string;
  transitComplete?: boolean;
};

const SELECTED_FIELD: string = "selected";

type ExportColumn = {
  accessorKey: string;
  header: string;
};

function getHeaderLabel(header: unknown): string {
  if (typeof header === "string") {
    return header;
  }
  return "";
}

type ToolbarProps = {
  loading: boolean;
  hasSelectedTickets: boolean;
  canDelete: boolean;
  canUnlink: boolean;
  hasTickets: boolean;
  handleDelete: () => void;
  handleUnlink: () => void;
  handleZeroOutTicket: () => void;
  getSelectedTickets: (
    ticketList: TGridTicket[],
    selectionState: { [id: string]: boolean | number[] }
  ) => TGridTicket[];
  exportTicketsToExcel: (
    data: TGridTicket[],
    exportColumns: ExportColumn[],
    fileName?: string
  ) => void;
  tickets: TGridTicket[];
  selectedState: { [id: string]: boolean | number[] };
  columns: ColumnDef<TGridTicket, string>[];
};

const Toolbar = ({
  loading,
  hasSelectedTickets,
  canDelete,
  canUnlink,
  hasTickets,
  handleDelete,
  handleUnlink,
  handleZeroOutTicket,
  getSelectedTickets,
  exportTicketsToExcel,
  tickets,
  selectedState,
  columns
}: ToolbarProps) => (
  <NavBar pectenArea="none" style={{ backgroundColor: "none" }}>
    <Menu
      mode="horizontal"
      disabledOverflow
      style={{ padding: 10, gap: 10, backgroundColor: "none" }}>
      <Button
        title="Delete"
        size="xsmall"
        disabled={loading || !hasSelectedTickets}
        hidden={!canDelete}
        icon={<TrashAltSolid />}
        onClick={handleDelete}
        sentiment="negative">
        Delete
      </Button>
      <Button
        title="Unlink"
        size="xsmall"
        disabled={loading || !hasSelectedTickets}
        hidden={!canUnlink}
        icon={<CrossCircle />}
        onClick={handleUnlink}
        sentiment="negative">
        Unlink
      </Button>
      <Button
        title="Export Selected to Excel"
        disabled={loading || !hasSelectedTickets}
        size="xsmall"
        hidden={!hasTickets}
        icon={<FileXlsOutline />}
        onClick={() => {
          const selectedTickets = getSelectedTickets(tickets, selectedState);
          exportTicketsToExcel(
            selectedTickets,
            columns
              .filter(
                (col): col is { accessorKey: string; header: string } =>
                  "accessorKey" in col && !!col.accessorKey
              )
              .map(col => ({
                accessorKey: String(col.accessorKey),
                header: typeof col.header === "string" ? col.header : "" // fallback if not string
              }))
          );
        }}
        sentiment="positive">
        Export Selected to Excel
      </Button>
      <Button
        title="Zero Out Volumes"
        disabled={loading || !hasSelectedTickets}
        hidden={!canUnlink}
        icon={<ArrowCcwCircle />}
        size="xsmall"
        onClick={handleZeroOutTicket}
        sentiment="negative">
        Zero Out
      </Button>
    </Menu>
  </NavBar>
);

type SelectionHeaderCheckboxProps = Readonly<{
  currentPageTickets: TGridTicket[];
  selectedState: { [id: string]: boolean | number[] };
  setSelectedState: React.Dispatch<React.SetStateAction<{ [id: string]: boolean | number[] }>>;
}>;

function SelectionHeaderCheckbox({
  currentPageTickets,
  selectedState,
  setSelectedState
}: SelectionHeaderCheckboxProps) {
  return (
    <div style={{ paddingLeft: "5px" }}>
      <Checkbox
        size={Sizes.ExtraSmall}
        checked={
          currentPageTickets.length > 0 &&
          currentPageTickets.every(ticket => selectedState[ticket.id])
        }
        indeterminate={
          currentPageTickets.some(ticket => selectedState[ticket.id]) &&
          !currentPageTickets.every(ticket => selectedState[ticket.id])
        }
        onChange={e => {
          const checked = (e.target as HTMLInputElement).checked;
          const pageTicketIds = currentPageTickets.map(ticket => ticket.id);

          setSelectedState(prevState => {
            if (checked) {
              return {
                ...prevState,
                ...Object.fromEntries(pageTicketIds.map(id => [id, true]))
              };
            } else {
              const newState = { ...prevState };
              pageTicketIds.forEach(id => {
                delete newState[id];
              });
              return newState;
            }
          });
        }}
      />
    </div>
  );
}

function selectionHeaderCheckboxRenderer(props: SelectionHeaderCheckboxProps) {
  return (
    <SelectionHeaderCheckbox
      currentPageTickets={props.currentPageTickets}
      selectedState={props.selectedState}
      setSelectedState={props.setSelectedState}
    />
  );
}

type SelectionCheckboxCellProps = Readonly<{
  row: Row<TGridTicket>;
  selectedState: { [id: string]: boolean | number[] };
  setSelectedState: React.Dispatch<React.SetStateAction<{ [id: string]: boolean | number[] }>>;
}>;

function SelectionCheckboxCell({
  row,
  selectedState,
  setSelectedState
}: SelectionCheckboxCellProps) {
  const ticket = row.original;
  const disabled =
    ticket.status === "LINKED" && (!ticket.movements?.length || ticket.transitComplete);
  return (
    <div style={{ paddingLeft: "5px" }}>
      <Checkbox
        size={Sizes.ExtraSmall}
        checked={!!selectedState[ticket.id]}
        disabled={disabled}
        hidden={disabled}
        onChange={e => {
          const checked = (e.target as HTMLInputElement).checked;
          setSelectedState(prev => ({
            ...prev,
            [ticket.id]: checked
          }));
        }}
      />
    </div>
  );
}

function selectionCheckboxCellRenderer(props: SelectionCheckboxCellProps) {
  return (
    <SelectionCheckboxCell
      row={props.row}
      selectedState={props.selectedState}
      setSelectedState={props.setSelectedState}
    />
  );
}

export const TicketingGrid = ({
  tickets,
  onRefresh,
  onDelete,
  onUnlink,
  onZeroOut,
  loading
}: {
  tickets: TGridTicket[];
  onRefresh: () => void;
  onDelete: (ticketsIds: string[]) => void;
  onUnlink: (ticketUnlinkInput: TGridTicket[]) => void;
  onZeroOut: (ticketZeroOutInput: TGridTicket[]) => void;
  loading: boolean;
}) => {
  const [selectedState, setSelectedState] = useState<{ [id: string]: boolean | number[] }>({});
  const [showEditTicketForm, setShowEditTicketForm] = useState<boolean>(false);
  const [editTicketForm, setEditTicketForm] = useState<TTicket>();

  const getSelectedTicketIds = useCallback(() => {
    return Object.entries(selectedState)
      .filter(([_, value]) => value)
      .map(([key]) => key);
  }, [selectedState]);

  const handleDelete = () => {
    const selectedTicketIds = getSelectedTicketIds();
    if (selectedTicketIds.length > 0) {
      setSelectedState({});
      onDelete(selectedTicketIds);
    }
  };

  const handleZeroOutTicket = () => {
    const selectedTicketIds = getSelectedTicketIds();
    if (selectedTicketIds.length > 0) {
      const input = tickets.filter(
        t => selectedTicketIds.includes(t.id) && t.movements?.length
      );
      if (input?.length) {
        onZeroOut(input);
      }
    }
  };

  const handleUnlink = () => {
    const selectedTicketIds = getSelectedTicketIds();
    if (selectedTicketIds.length > 0) {
      const input = tickets.filter(
        t => selectedTicketIds.includes(t.id) && t.movements?.length
      );
      onUnlink(input);
    }
  };

  function getNestedValue(obj: unknown, path: string): unknown {
    return path.split(".").reduce((acc, prop) => {
      if (acc && typeof acc === "object") {
        return (acc as Record<string, unknown>)[prop];
      }
      return "";
    }, obj);
  }

  function buildRow(ticket: TGridTicket, headers: { key: string }[]): Record<string, unknown> {
    const row: Record<string, unknown> = {};
    headers.forEach(({ key }) => {
      row[key] = getNestedValue(ticket, key);
    });
    return row;
  }

  function exportTicketsToExcel(
    data: TGridTicket[],
    exportColumns: ExportColumn[],
    fileName = "tickets.xlsx"
  ) {
    const headers = exportColumns
      .filter(col => col.accessorKey && col.header)
      .map(col => ({
        label: typeof col.header === "string" ? col.header : "",
        key: col.accessorKey
      }));

    const rows = data.map(ticket => buildRow(ticket, headers));

    const worksheet = utils.json_to_sheet(rows, {
      header: headers.map(h => h.key)
    });

    utils.sheet_add_aoa(worksheet, [headers.map(h => h.label)], { origin: "A1" });

    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Tickets");

    writeFile(workbook, fileName);
  }

  function getSelectedTickets(
    ticketList: TGridTicket[],
    selectionState: { [id: string]: boolean | number[] }
  ) {
    const selectedIds = Object.entries(selectionState)
      .filter(([_, value]) => value)
      .map(([key]) => key);
    return ticketList.filter(ticket => selectedIds.includes(ticket.id));
  }

  const hasTickets = !!tickets?.length;
  const hasSelectedTickets = Object.entries(selectedState).some(([_, value]) => value);
  const canUnlink =
    tickets.length > 0 &&
    tickets.some(t => t.status === "LINKED" && t.movements?.length && !t.transitComplete);
  const canDelete = tickets.some(t => t.status !== "LINKED");

  const calculateWidth = useCallback(
    (accessorKey: string, header: string, format?: string) => {
      let maxWidth = calculateSize(header, FONT_OPTIONS).width + PADDING;
      if (tickets && accessorKey) {
        tickets.forEach(item => {
          const getValue = propertyGetter(accessorKey);
          const value = getValue(item);
          const formattedValue =
            value instanceof Date && format ? formatDateForDisplay(value) : String(value);
          const size = calculateSize(formattedValue, FONT_OPTIONS).width + PADDING;
          maxWidth = Math.max(maxWidth, size);

          if (size > maxWidth) {
            maxWidth = size;
          }
        });
      }
      return maxWidth;
    },
    [tickets]
  );

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });

  const startIdx = pagination.pageIndex * pagination.pageSize;
  const endIdx = startIdx + pagination.pageSize;
  const currentPageTickets = tickets.slice(startIdx, endIdx);

  const selectionColumn: ColumnDef<TGridTicket, string> = {
    header: () =>
      selectionHeaderCheckboxRenderer({
        currentPageTickets,
        selectedState,
        setSelectedState
      }),
    id: "0",
    accessorKey: SELECTED_FIELD,
    size: 60,
    minSize: 50,
    maxSize: 100,
    cell: ({ row }) =>
      selectionCheckboxCellRenderer({
        row,
        selectedState,
        setSelectedState
      })
  };

  const renderEditHeader = () => <span>&nbsp;</span>;

  const renderEditCell = ({ row }: CellContext<TGridTicket, string>) => {
    const ticket = row.original;
    const disabled =
      ticket.status === "LINKED" && (!ticket.movements?.length || ticket.transitComplete);

    return (
      <Button
        size="xsmall"
        variant="transparent"
        icon={<Edit />}
        onClick={() => {
          const gridTicket = row.original as TGridTicket;
          const editedTicket: TTicket = {
            ...gridTicket,
            railcars: []
          };
          setEditTicketForm(editedTicket);
          setShowEditTicketForm(true);
        }}
        sentiment="negative"
        iconOnly
        hidden={disabled}
        disabled={disabled}
      />
    );
  };

  const columns = useMemo<ColumnDef<TGridTicket, string>[]>(
    () => [
      selectionColumn,
      {
        id: "1",
        header: renderEditHeader,
        accessorKey: "edit",
        size: 60,
        minSize: 50,
        maxSize: 70,
        cell: renderEditCell
      },
      {
        id: "2",
        header: "Source",
        accessorKey: "source",
        size: calculateWidth("source", "Source")
      },
      {
        id: "3",
        header: "Ticket Number",
        accessorKey: "ticketNumber",
        size: calculateWidth("ticketNumber", "Ticket Number")
      },
      {
        id: "4",
        header: "Ticket Date",
        accessorKey: "startDate",
        size: calculateWidth("startDate", "Ticket Date", "MM/DD/YY")
      },
      {
        id: "5",
        header: "Last Updated By",
        accessorKey: "lastModifiedBy.id",
        size: calculateWidth("lastModifiedBy.id", "Last Updated By")
      },
      {
        id: "6",
        header: "Last Updated At",
        accessorKey: "lastModifiedDate",
        size: calculateWidth("lastModifiedDate", "Last Updated At", "MM/DD/YY HH:mm:ss")
      },
      {
        id: "7",
        header: "Status",
        accessorKey: "status",
        size: calculateWidth("status", "Status")
      },

      {
        id: "8",
        header: "Net Volume",
        accessorKey: "netVolume",
        cell: ({ row }) => row.original.netVolume?.toFixed(4),
        size: calculateWidth("netVolume", "Net"),
        meta: {
          className: "!k-text-right",
          exportFormat: "0.0###"
        }
      },
      {
        id: "9",
        header: "Gross Volume",
        accessorKey: "grossVolume",
        cell: ({ row }) => row.original.grossVolume?.toFixed(4),
        size: calculateWidth("grossVolume", "Gross"),
        meta: {
          className: "!k-text-right",
          exportFormat: "0.0###"
        }
      },
      {
        id: "10",
        header: "Volume Unit",
        accessorKey: "volumeUnit",
        size: calculateWidth("volumeUnit", "Unit")
      },

      {
        id: "11",
        header: "Ship From",
        accessorKey: "shipFromCode.description",
        size: calculateWidth("shipFromCode.description", "Ship From")
      },
      {
        id: "12",
        header: "Ship To",
        accessorKey: "shipToCode.description",
        size: calculateWidth("shipToCode.description", "Ship To")
      },
      {
        id: "13",
        header: "Batch",
        accessorKey: "batch.name",
        size: calculateWidth("batch.name", "Batch")
      },
      {
        id: "14",
        header: "MoT",
        accessorKey: "modeOfTransport.name",
        size: calculateWidth("modeOfTransport.name", "MoT")
      },
      {
        id: "15",
        header: "Location",
        accessorKey: "facility.name",
        size: calculateWidth("facility.name", "Location")
      },
      {
        id: "16",
        header: "Product",
        accessorKey: "product.name",
        size: calculateWidth("product.name", "Product")
      },
      {
        id: "17",
        header: "Carrier",
        accessorKey: "carrier.name",
        size: calculateWidth("carrier.name", "Carrier")
      },
      {
        id: "18",
        header: "Logistics System",
        accessorKey: "logisticsSystem.name",
        size: calculateWidth("logisticsSystem.name", "Logistics System")
      },
      {
        id: "19",
        header: "Railcars",
        accessorKey: "railcars",
        size: calculateWidth("railcars", "Railcars")
      },
      {
        id: "20",
        header: "Border Crossing Date",
        accessorKey: "borderCrossingDate",
        cell: ({ row }) =>
          row.original.borderCrossingDate
            ? dayjs(row.original.borderCrossingDate).format("MM/DD/YY")
            : "",
        size: calculateWidth("borderCrossingDate", "Border Crossing Date", "MM/DD/YY")
      },
      {
        id: "21",
        header: "PO Number",
        accessorKey: "poNumber",
        size: calculateWidth("poNumber", "PO Number")
      },
      {
        id: "22",
        header: "Invoice Comment",
        accessorKey: "invoiceComment",
        size: calculateWidth("invoiceComment", "Invoice Comment")
      },
      {
        id: "23",
        header: "File Name",
        accessorKey: "document.fileName",
        size: calculateWidth("document.fileName", "File Name")
      },
      {
        id: "24",
        header: "Page Numbers",
        accessorKey: "pageNumbers",
        size: calculateWidth("pageNumbers", "Page Numbers")
      }
    ],
    [calculateWidth, selectedState]
  );

  const [sorting, setSorting] = useState<SortingState>([]);

  const tableOptions: IBaseTableProps<TGridTicket>["tableOptions"] = {
    defaultColumn: {
      minSize: 200,
      maxSize: 450
    },
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: {
      sorting,
      pagination
    },
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    autoResetPageIndex: false
  };

  return (
    <div>
      <Flexbox
        flexDirection="column"
        style={{
          flex: 1,
          overflow: "auto",
          maxHeight: "100%"
        }}>
        <div>
          <Toolbar
            loading={loading}
            hasSelectedTickets={hasSelectedTickets}
            canDelete={canDelete}
            canUnlink={canUnlink}
            hasTickets={hasTickets}
            handleDelete={handleDelete}
            handleUnlink={handleUnlink}
            handleZeroOutTicket={handleZeroOutTicket}
            getSelectedTickets={getSelectedTickets}
            exportTicketsToExcel={exportTicketsToExcel}
            tickets={tickets}
            selectedState={selectedState}
            columns={columns}
          />
        </div>
        <BaseTable
          noFooter
          className="no-left-indent"
          columns={columns}
          data={tickets}
          tableOptions={tableOptions}
          style={{
            width: "100%",
            tableLayout: "fixed"
          }}
        />
      </Flexbox>

      {showEditTicketForm && (
        <AddOrUpdateTicket
          onCancel={() => setShowEditTicketForm(false)}
          onSuccess={() => {
            setShowEditTicketForm(false);
            onRefresh();
          }}
          activeTicket={editTicketForm}
        />
      )}
    </div>
  );
};

export default TicketingGrid;
